import { Three3DScene } from 'redhotmagma-visualization/Three/Three3DScene';
import { EventTypes } from 'redhotmagma-visualization/Constants';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { canvasRGBA } from 'stackblur-canvas';
import envMapSource from './resources/driving_school_1024x512.hdr';
import {
  ACESFilmicToneMapping,
  BasicShadowMap,
  Box3,
  CanvasTexture,
  CircleGeometry,
  DirectionalLight,
  Group,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  PlaneGeometry,
  PMREMGenerator,
  ShadowMaterial,
  Sphere,
  Vector2,
  Vector3,
  WebGLRenderTarget,
  DoubleSide,
  MathUtils,
} from 'three';

const SHADOW_MAP_SIZE = 64;
const GROUND_SCALING = 2;
const SHADOW_TEXTURE_SCALING_W = 0.8;
const SHADOW_TEXTURE_SCALING_H = 1;

function setLightShadow(light, d = 50) {
  if (light.isDirectionalLight) {
    light.castShadow = true;
    light.shadow.mapSize.width = SHADOW_MAP_SIZE;
    light.shadow.mapSize.height = SHADOW_MAP_SIZE;
    light.shadow.camera.top = d;
    light.shadow.camera.bottom = -d;
    light.shadow.camera.left = -d;
    light.shadow.camera.right = d;
    light.shadow.camera.near = 0.0001;
    light.shadow.camera.far = 2 * d + 0.0001;
    light.shadow.camera.updateProjectionMatrix();
  }
}

function copyRenderTargetToCanvas(renderer, renderTarget, dstCanvas) {
  const { width, height } = renderTarget;
  const pixelBuffer = new Uint8ClampedArray(4 * width * height);
  renderer.readRenderTargetPixels(renderTarget, 0, 0, width, height, pixelBuffer);
  const imageData = new ImageData(pixelBuffer, width, height);
  const canvas = dstCanvas || document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

function applyShadowEffects(canvas) {
  const { width, height } = canvas;
  const blurCanvas = document.createElement('canvas');
  blurCanvas.width = width;
  blurCanvas.height = height;
  const blurCtx = blurCanvas.getContext('2d');
  const shadowTextureOffsetW = (1 - SHADOW_TEXTURE_SCALING_W) * 0.5;
  const shadowTextureOffsetH = (1 - SHADOW_TEXTURE_SCALING_H) * 0.5;
  blurCtx.globalAlpha = 0.6;
  blurCtx.drawImage(
    canvas,
    width * shadowTextureOffsetW,
    height * shadowTextureOffsetH,
    width * SHADOW_TEXTURE_SCALING_W,
    height * SHADOW_TEXTURE_SCALING_H
  );
  canvasRGBA(blurCanvas, 0, 0, width, height, width * 0.05);
  return blurCanvas;
}

export class Visualization extends Three3DScene {
  cameraPosition = new Vector3(0, 0, 4);
  defaultRelativeCameraPosition = new Vector3(-3.2, 0.8, -2).normalize();
  relativeFrontPosition = new Vector3(0, 0, -1);
  relativeSidePosition = new Vector3(-1, 0, 0);
  relativeScreenshotPosition = new Vector3(-3.2, 0.8, -2).normalize();
  // "Im Raum ansehen": elevated head-at-back angle for milieus whose head-wall faces the viewer
  // (coastal/mountain/pacific). Captured from a live orbit; see milieus.js bedView mapping.
  relativeRoomPosition = new Vector3(0.5842, 0.2965, -0.7555).normalize();

  setupDefaultControls() {
    super.setupDefaultControls();
    this.controls.maxPolarAngle = Math.PI * 0.5;
  }

  createScene() {
    const scene = super.createScene();
    //scene.background = new Color(0xf2f2f2);
    scene.environment = this.envMap;

    const dirLight = new DirectionalLight(0xffffff, 0.00001);
    dirLight.position.set(0, 1, 0);
    setLightShadow(dirLight);
    this.shadowLight = dirLight;
    scene.add(dirLight);

    return scene;
  }

  createLoader(options = {}) {
    const loadingManager = this.createLoadingManager(options);

    if (!this.dracoLoader) {
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath('libs/draco/gltf/');
      this.dracoLoader = dracoLoader;
    }

    const loader = new GLTFLoader(loadingManager);
    loader.setDRACOLoader(this.dracoLoader);
    return loader;
  }

  getSceneContainerBox() {
    const box = new Box3();
    this.sceneContainer.traverse((object) => {
      if (object.isMesh && object.name !== 'ground') {
        box.expandByObject(object);
      }
    });
    return box;
  }

  setCameraPositionToCurrentDesignArea({ getViewOffset }) {
    const { offsetX, offsetY, width, height, fullWidth, fullHeight } = getViewOffset();
    this.camera.setViewOffset(width, height, offsetX, offsetY, fullWidth, fullHeight);
  }

  createRenderer() {
    const renderer = super.createRenderer();
    renderer.antialias = true;
    renderer.toneMapping = ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.useLegacyLights = true;
    renderer.shadowMap.type = BasicShadowMap;
    renderer.shadowMap.enabled = true;

    const pmremGenerator = new PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();

    this.envMapPromise = new Promise((resolve, reject) => {
      new RGBELoader().load(
        envMapSource,
        (texture) => {
          this.envMap = pmremGenerator.fromEquirectangular(texture).texture;
          this.scene.environment = this.envMap;
          texture.dispose();
          pmremGenerator.dispose();
          resolve(this.envMap);
        },
        undefined,
        reject
      );
    });

    return renderer;
  }

  setLightShadow() {
    return null;
  }

  createDirLight() {
    return new Object3D();
  }

  createSceneLights() {
    const sun = new DirectionalLight(0xffffff);
    sun.position.set(-3, 10, -10);
    const sceneLights = new Group();
    sceneLights.add(sun);
    this.sceneLights = sceneLights;
    return sceneLights;
  }

  createHemiLight() {
    return new Object3D();
  }

  createGround() {
    if (!this.shadowGround) {
      const shadowGeometry = new PlaneGeometry(1, 1);
      shadowGeometry.rotateX(-Math.PI * 0.5);
      shadowGeometry.rotateY(Math.PI);
      this.shadowGround = new Mesh(
        shadowGeometry,
        new ShadowMaterial({
          color: 0xffffff,
        })
      );
    }

    if (!this.ground) {
      const geometry = new CircleGeometry(1, 32);
      geometry.rotateX(-Math.PI * 0.5);
      this.ground = new Mesh(geometry, new MeshStandardMaterial({ color: 0xffffff }));
      this.ground.visible = false;
    }

    return this.ground;
  }

  createLights() {
    return null;
  }

  render() {
    if (this._disableRender) {
      return;
    }
    super.render();
  }

  async loadSceneContainer(item) {
    const result = await super.loadSceneContainer(item);
    // hide the ground included in the models
    const ground = result.scene.getObjectByName('ground');
    if (ground) {
      ground.visible = false;
    }
    result.scene.traverse((object) => {
      if (
        object.name.toLowerCase().startsWith('circle') ||
        object.material?.name.toLowerCase().startsWith('ground')
      ) {
        object.visible = false;
        object.parent.remove(object);
      }
    });

    Object.assign(result.scene.userData, item);
    return result;
  }

  setupGroundAndShadow() {
    if (!this.scene) {
      return;
    }

    if (!this.sceneContainer.children.length) {
      return;
    }

    // activate shadows and disable other lights
    this.sceneContainer.traverse((object) => {
      if (object.isMesh) {
        object.castShadow = true;
        if (object.material) {
          object.material.shadowSide = DoubleSide;
        }
      }
      if (object.isLight) {
        object.visible = false;
      }
    });

    // remember the original values and set them to null (we need a white background)
    const { environment, background } = this.scene;
    this.scene.environment = null;
    this.scene.background = null;

    // get bounding parameters for positioning and scaling
    this.sceneContainer.updateWorldMatrix(true, true);
    const box = this.getSceneContainerBox();
    const sphere = box.getBoundingSphere(new Sphere());
    const { radius } = sphere;

    // set the ground positions right below the model bounding box
    this.ground.position.set(sphere.center.x, box.min.y - 0.01, sphere.center.z);
    this.shadowGround.position.copy(this.ground.position);
    this.shadowGround.position.y += 0.01;

    // make the shadowGround receive shadows
    this.shadowGround.material = new ShadowMaterial();
    this.shadowGround.receiveShadow = true;
    this.scene.add(this.shadowGround);

    // set the scaling of the ground objects
    const circleGroundScaling = Math.max(GROUND_SCALING * radius * 10, 35);
    this.ground.scale.set(circleGroundScaling, 1, circleGroundScaling);
    this.shadowGround.scale.set(GROUND_SCALING * radius * 1.8, 1, GROUND_SCALING * radius * 1.8);

    // activate the shadowLight and set the parameters
    this.shadowLight.visible = true;
    this.shadowLight.target = this.shadowGround;
    this.shadowLight.position.set(sphere.center.x, sphere.center.y + radius, sphere.center.z);
    setLightShadow(this.shadowLight, radius * 2);
    this.shadowLight.shadow.camera.far = this.shadowLight.position.y - this.shadowGround.position.y + 0.05;
    this.shadowLight.shadow.camera.updateProjectionMatrix();

    // calculate the canvas size for the rendered shadow
    const width = SHADOW_MAP_SIZE * GROUND_SCALING;
    const height = SHADOW_MAP_SIZE * GROUND_SCALING;

    // create and set the new render target
    const renderTarget = new WebGLRenderTarget(width, height);
    this.renderer.setRenderTarget(renderTarget);

    // create a camera to capture only the shadowGround plane (with the shadow)
    const shadowCamera = this.shadowLight.shadow.camera.clone();
    this.scene.add(shadowCamera);
    shadowCamera.position.copy(this.shadowGround.position);
    shadowCamera.position.y = this.shadowGround.position.y + 0.01;
    shadowCamera.far = 0.015;
    shadowCamera.updateProjectionMatrix();
    shadowCamera.lookAt(this.shadowGround.position);
    this.renderer.render(this.scene, shadowCamera);
    this.scene.remove(shadowCamera);

    // apply blur effect on the captured shadow
    const shadowCanvas = copyRenderTargetToCanvas(this.renderer, renderTarget);
    const blurredCanvas = applyShadowEffects(shadowCanvas);

    // apply the created shadow as texture map
    this.shadowGround.material = new MeshStandardMaterial({
      map: new CanvasTexture(blurredCanvas),
      transparent: true,
    });
    this.shadowGround.receiveShadow = false;

    // turn off shadows, activate lights
    this.sceneContainer.traverse((object) => {
      if (object.isMesh) {
        object.castShadow = false;
      }
      if (object.isLight) {
        object.visible = true;
      }
    });

    // restore original values
    this.scene.environment = environment;
    this.scene.background = background;

    // deactivate shadowLight
    this.shadowLight.visible = false;

    // reset render target
    this.renderer.setRenderTarget(null);
  }

  addLightsIfNeeded(scene) {
    if (!this.scene) {
      return;
    }
    const sceneHasLight = scene.getObjectByProperty('isLight', true);
    if (!sceneHasLight && !this.sceneLights) {
      this.scene.add(this.createSceneLights());
    } else if (sceneHasLight) {
      // remove if the scene shipped its own light
      this.scene.remove(this.sceneLights);
      delete this.sceneLights;
    }
  }

  async loadScene(resourceList) {
    // fix LoadFinished event by trapping the loadScene calls to finish the current one before starting the next one
    await this._loadScenePromise;
    this._loadScenePromise = this._loadScene(resourceList);
    return await this._loadScenePromise;
  }

  async _loadScene(resourceList) {
    this._disableRender = true;
    const patchedResourceList = resourceList.map((item) => ({
      ...item,
      model: item.model && item.position ? `${item.model}?p=${item.position}` : item.model,
    }));
    const result = await super.loadScene(patchedResourceList);
    this.setupGroundAndShadow();

    // make sure no rendering happens before the envMap is loaded
    try {
      await this.envMapPromise;
    } catch (e) {
      // ignore
    }

    this._disableRender = false;
    this.render();
    this.onLoadFinished();
    return result;
  }

  onLoadFinished = async () => {
    await this._loadScenePromise;
    this.observable.dispatch(EventTypes.LoadFinished, {
      screenshot: !!this._screenshottingInProgress,
    });
  };

  initCustomizableMaterialsIfNeeded() {
    // prevent the core function from using CanvasTexture
  }

  calculateRelativeCameraView(center, relativePosition, distance) {
    return center
      .clone()
      .add(
        relativePosition.clone().multiplyScalar(distance || this._fitDistance || this.controls.minDistance)
      );
  }

  async initCameraViews(cameras) {
    if (cameras) {
      this._lastCameras = cameras;
    } else if (this._lastCameras) {
      cameras = this._lastCameras;
    }
    if (!cameras) {
      return;
    }
    if (!this.sceneContainer?.children?.length) {
      return;
    }
    if (!this.controls) {
      return;
    }

    const box = this.getSceneContainerBox();
    const size = box.getSize(new Vector3());
    if (!size.length()) {
      return;
    }
    const center = new Vector3();
    box.getCenter(center);

    const currentCameraName = this.currentCameraView;
    const previousCameraView = this.cameraViews[currentCameraName];

    this.setupRelativeCameras(center);
    this.setupSceneCameras(cameras, center);

    const frustumFactor = Math.tan(MathUtils.degToRad(this.camera.fov * 0.5));
    const frontDistance = (box.max.y / frustumFactor + box.max.x) * 0.9;
    const sideDistance = box.max.x / this.camera.aspect / frustumFactor + box.max.z;

    const position = this.calculateRelativeCameraView(
      center,
      this.defaultRelativeCameraPosition,
      frontDistance
    );
    this.cameraPosition.copy(position).normalize();
    this.cameraViews.default = {
      position,
      lookat: center.clone(),
      orientation: new Vector3(0, 1, 0),
    };
    this.cameraViews.front = {
      position: this.calculateRelativeCameraView(center, this.relativeFrontPosition, frontDistance),
      lookat: center.clone(),
      orientation: new Vector3(0, 1, 0),
    };
    this.cameraViews.side = {
      position: this.calculateRelativeCameraView(center, this.relativeSidePosition, sideDistance),
      lookat: center.clone(),
      orientation: new Vector3(0, 1, 0),
    };

    if (!this._firstCameraInitDone) {
      this._firstCameraInitDone = true;
      this.currentCameraView = null;
      this.jumpToView('default', false);
    } else if (
      previousCameraView &&
      this.cameraViews[currentCameraName] &&
      this.shouldViewChangeTriggerJumpToView(previousCameraView, this.cameraViews[currentCameraName])
    ) {
      this.currentCameraView = null;
      this.jumpToView(currentCameraName, true);
    }
  }

  updateScreenshotCameraViews(center) {
    const box = this.getSceneContainerBox();
    const radius = box.getBoundingSphere(new Sphere()).radius;
    const fitOffset = 0.9;
    const fitDistance = this.getFitDistance(radius) * fitOffset;
    this.screenshotCameraViews = {
      default: {
        ...this.cameraViews.default,
        position: center
          .clone()
          .add(this.relativeScreenshotPosition.clone().multiplyScalar(fitDistance * 0.8)),
      },
      front: {
        ...this.cameraViews.front,
        position: center.clone().add(this.relativeFrontPosition.clone().multiplyScalar(fitDistance * 0.7)),
      },
      side: {
        ...this.cameraViews.side,
        position: center.clone().add(this.relativeSidePosition.clone().multiplyScalar(fitDistance * 0.8)),
      },
      room: {
        ...this.cameraViews.default,
        position: center.clone().add(this.relativeRoomPosition.clone().multiplyScalar(fitDistance * 0.9015)),
      },
    };
  }

  async getScreenshots(callback, { variantList, size }) {
    if (!this.renderer) {
      return callback({});
    }
    this._screenshottingInProgress = true;
    this.endAnimation();
    await this.animationRunning();
    const removeTempOverlay = await this.createTempOverlay();

    // store original state values
    const origVariantPath = this._materialVariantPath;
    const origPosition = this.camera.position.clone();
    const origOrientation = this.camera.up.clone();
    const origTarget = this.controls ? this.controls.target.clone() : false;
    const origMinDistance = this.controls.minDistance;
    const origSize = new Vector2();
    this.renderer.getSize(origSize);
    const origPixelRatio = this.renderer.getPixelRatio();

    const restoreOriginalValues = async () => {
      this.controls.minDistance = origMinDistance;
      this._screenshottingInProgress = false;
      this.renderer.setSize(origSize.width, origSize.height);
      this.renderer.setPixelRatio(origPixelRatio);
      this.updateCamera(origSize.width, origSize.height);
      this.setCameraPosition(origPosition, origOrientation, origTarget);
      this._materialVariantPath = origVariantPath;
      await this.updateMaterial();
      this.render();
      removeTempOverlay();
    };

    const restoreScreenshotSetup = await this.setupScreenshots();

    // get the size from the size param, or use default values
    const width = size?.width || 1024;
    const height = size?.height || 768;

    // set the camera to the screenshot size
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(width, height);
    this.updateCamera(width, height);
    this.camera.clearViewOffset();

    this.controls.minDistance = 0;
    this.updateScreenshotCameraViews(origTarget);

    const screenshots = {};
    const views = this.screenshotCameraViews || this.cameraViews;
    // if the viewList option is provided, use it as keys instead of the list of all views
    //const viewKeyList = viewList instanceof Array && viewList.length > 0 ? viewList : Object.keys(views);
    const viewKeyList = Object.keys(views);

    for (let variant of variantList) {
      if (this._abortScreenshotting) {
        return await restoreOriginalValues();
      }

      const data = {};
      this.setMaterialVariantPath(variant);

      try {
        await this.updateMaterial(this.scene, { screenshot: true });
      } catch (e) {
        // ignore errors
        continue;
      }

      for (let viewKey of viewKeyList) {
        if (this._abortScreenshotting) {
          return await restoreOriginalValues();
        }

        const view = views[viewKey];
        const {
          position,
          orientation,
          lookat: target,
        } = {
          ...this.cameraViews.default,
          ...view,
        };
        this.setCameraPosition(position, orientation, target);
        this.render();

        data[viewKey] = this.renderer.domElement.toDataURL('image/png');
      }
      screenshots[variant] = data;
    }

    await restoreScreenshotSetup();

    await restoreOriginalValues();

    callback(screenshots);
  }

  getFitDistance(radius, camera = this.camera) {
    return radius / camera.aspect / Math.tan(MathUtils.degToRad(camera.fov * 0.5)) + radius;
  }

  fitSceneToCamera() {
    const { controls, camera } = this;
    const box = this.getSceneContainerBox();
    const fitOffset = 1;

    const sphere = new Sphere();
    box.getBoundingSphere(sphere);

    if (sphere.radius < 0.001) {
      return;
    }

    const fitDistance = this.getFitDistance(sphere.radius);
    this._fitDistance = fitDistance;
    const minDistance = (fitDistance - sphere.radius * 0.5) * fitOffset;

    const maxDistance = minDistance * 2;

    // update controls
    if (minDistance === controls.minDistance) {
      return;
    }

    this.screenshotDistance = fitDistance * 0.8;
    controls.minDistance = minDistance;
    controls.maxDistance = maxDistance;
    controls.enablePan = false;
    camera.near = minDistance * 0.01;
    camera.far = maxDistance * 100;
    camera.position.copy(camera.position.normalize().multiplyScalar(minDistance));
    camera.updateProjectionMatrix();
    controls.target = sphere.center;

    controls.update();
  }

  setSize(params, rerender = true) {
    super.setSize(params, rerender);
    this.fitSceneToCamera();
    this.initCameraViews();
  }

  setupRelativeCameras(center) {
    if (!this.relativeCameraViews.default) {
      this.relativeCameraViews.default = {
        position: this.defaultCameraPosition.clone(),
        lookat: new Vector3(0, 0, 0),
        orientation: new Vector3(0, 1, 0),
      };
    }
    this.cameraViews = this.calculateRelativeViews(
      this.relativeCameraViews,
      center,
      this.controls.minDistance
    );

    if (!this.relativeScreenshotViews.default) {
      this.relativeScreenshotViews.default = {
        position: this.defaultScreenshotPosition.clone(),
        lookat: new Vector3(0, 0, 0),
        orientation: new Vector3(0, 1, 0),
      };
    }

    this.screenshotViews = this.calculateRelativeViews(
      this.relativeScreenshotViews,
      center,
      this.controls.minDistance
    );
  }
}

export default Visualization;
